import { useState, useEffect, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Tv, Clock, Calendar, ChevronLeft, ChevronRight, Radio, Play, Video } from "lucide-react";
import { Button } from "./ui/button";
import { Helmet } from "react-helmet";
import axios from "axios";
import { CHANNEL_LOGOS } from "../constants/channelLogos";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const HOURS = Array.from({ length: 24 }, (_, i) => i);

const formatTime = (hour, minute = 0) => {
  const h = hour % 12 || 12;
  const ampm = hour < 12 ? "AM" : "PM";
  return `${h}:${minute.toString().padStart(2, "0")} ${ampm}`;
};

const getCurrentDayIndex = () => {
  const day = new Date().getDay();
  return day === 0 ? 6 : day - 1;
};

const getCurrentTimePosition = () => {
  const now = new Date();
  return ((now.getHours() * 60 + now.getMinutes()) / (24 * 60)) * 100;
};

// Get the date for a given day index relative to current week
const getDateForDay = (dayIndex) => {
  const today = new Date();
  const todayIndex = getCurrentDayIndex();
  const diff = dayIndex - todayIndex;
  const date = new Date(today);
  date.setDate(date.getDate() + diff);
  return date.toISOString().split("T")[0]; // YYYY-MM-DD
};

const getTeamName = (team) => typeof team === "object" ? (team?.name || team?.abbreviation || "TBD") : (team || "TBD");

const getYtVideoId = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
};

const PlaylistGuideItems = ({ items }) => {
  if (!items.length) return <div className="px-3 py-3 text-zinc-600 text-xs">No playlist items</div>;
  return (
    <div className="divide-y divide-white/5">
      {items.slice(0, 6).map((item, idx) => {
        const vid = getYtVideoId(item.url);
        return (
          <div key={idx} className="flex items-center gap-3 px-3 py-2 border-l-2 border-l-zinc-600">
            {vid ? (
              <img src={`https://img.youtube.com/vi/${vid}/mqdefault.jpg`} alt="" className="w-16 h-9 object-cover rounded-sm flex-shrink-0 bg-zinc-800" />
            ) : (
              <div className="w-16 h-9 bg-zinc-800 flex items-center justify-center flex-shrink-0 rounded-sm">
                <Video className="w-4 h-4 text-zinc-600" />
              </div>
            )}
            <span className="text-white text-xs truncate">{item.title || "Untitled"}</span>
          </div>
        );
      })}
      {items.length > 6 && (
        <div className="px-3 py-2 text-zinc-600 text-[10px]">+{items.length - 6} more in playlist</div>
      )}
    </div>
  );
};

export const GuidePage = () => {
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(getCurrentDayIndex());
  const [currentTime, setCurrentTime] = useState(getCurrentTimePosition());

  useEffect(() => {
    const fetchGuide = async () => {
      try {
        const response = await axios.get(`${API}/channels/guide/data`);
        setChannels(response.data?.channels || []);
      } catch (error) {
        console.error("Error fetching guide data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchGuide();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => setCurrentTime(getCurrentTimePosition()), 60000);
    return () => clearInterval(interval);
  }, []);

  // Parse broadcast time into hours/minutes
  const parseBroadcastTime = useCallback((timeStr) => {
    if (!timeStr) return null;
    const parts = timeStr.split(":");
    if (parts.length < 2) return null;
    return { hour: parseInt(parts[0], 10), minute: parseInt(parts[1], 10) };
  }, []);

  // Get all schedule items for a channel on a selected day
  const getChannelSlots = useCallback((channel) => {
    const slots = [];
    const selectedDate = getDateForDay(selectedDay);

    // 1) 24/7 schedule items (if channel has schedule_enabled)
    if (channel.schedule_enabled && channel.schedule_items) {
      for (const slot of channel.schedule_items) {
        if (slot.day === selectedDay || slot.repeat === "daily") {
          slots.push({
            id: slot.id || `sched-${slot.start_hour}-${slot.start_minute}`,
            title: slot.title,
            startMin: slot.start_hour * 60 + (slot.start_minute || 0),
            endMin: slot.end_hour * 60 + (slot.end_minute || 0),
            type: "schedule",
            sport: null
          });
        }
      }
    }

    // 2) Scheduled broadcasts for this channel on this date
    const broadcasts = channel.broadcasts || [];
    for (const b of broadcasts) {
      if (b.scheduled_date !== selectedDate) continue;
      const time = parseBroadcastTime(b.scheduled_time);
      if (!time) continue;
      const startMin = time.hour * 60 + time.minute;
      const duration = b.estimated_duration || 120; // default 2 hours
      const endMin = Math.min(startMin + duration, 24 * 60);
      const away = getTeamName(b.away_team);
      const home = getTeamName(b.home_team);
      const title = (away && home && away !== "TBD" && home !== "TBD")
        ? `${away} @ ${home}`
        : b.title || "Broadcast";

      slots.push({
        id: b.id,
        title,
        startMin,
        endMin,
        type: "broadcast",
        sport: b.sport,
        isLive: b.is_live,
        isCompleted: b.is_completed,
        broadcastId: b.id
      });
    }

    // 3) 24/7 playlist schedule (fills gaps around real broadcasts)
    if (channel.is_24_7_enabled && channel.playlist_schedule) {
      const daySchedule = channel.playlist_schedule[selectedDate] || [];
      // Build occupied ranges from real broadcasts/schedule
      const occupied = slots.map(s => ({ start: s.startMin, end: s.endMin }));
      
      for (const ps of daySchedule) {
        const time = parseBroadcastTime(ps.start_time);
        if (!time) continue;
        const startMin = time.hour * 60 + time.minute;
        const endMin = startMin + (ps.duration_min || 5);
        
        // Skip if overlapping with a real broadcast
        const overlaps = occupied.some(o => startMin < o.end && endMin > o.start);
        if (overlaps) continue;
        
        slots.push({
          id: `pl-${selectedDate}-${startMin}`,
          title: ps.title || "Playlist",
          startMin,
          endMin: Math.min(endMin, 24 * 60),
          type: "playlist",
          thumbnailUrl: ps.thumbnail_url,
          isPlaylist: true
        });
      }
    }

    return slots.sort((a, b) => a.startMin - b.startMin);
  }, [selectedDay, parseBroadcastTime]);

  // Check if a slot is currently active
  const isSlotActive = (slot) => {
    if (selectedDay !== getCurrentDayIndex()) return false;
    const now = new Date();
    const currentMin = now.getHours() * 60 + now.getMinutes();
    return currentMin >= slot.startMin && currentMin < slot.endMin;
  };

  // Get now-playing label
  const getNowPlaying = useCallback((channel) => {
    if (selectedDay !== getCurrentDayIndex()) return null;
    const slots = getChannelSlots(channel);
    const now = new Date();
    const currentMin = now.getHours() * 60 + now.getMinutes();
    const active = slots.find(s => currentMin >= s.startMin && currentMin < s.endMin);
    if (active) return active.title;
    if (channel.is_24_7_enabled && channel.fill_gaps_with_loop !== false) return "Loop Playlist";
    return null;
  }, [selectedDay, getChannelSlots]);

  const goToPrevDay = () => setSelectedDay(d => (d - 1 + 7) % 7);
  const goToNextDay = () => setSelectedDay(d => (d + 1) % 7);
  const goToToday = () => setSelectedDay(getCurrentDayIndex());
  const isToday = selectedDay === getCurrentDayIndex();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center">
        <div className="text-center">
          <Tv className="w-16 h-16 text-orange-500 mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-500">Loading program guide...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="guide-page">
      <Helmet>
        <title>ASN | Guide</title>
        <meta name="description" content="View the weekly program schedule for all channels on Antler Sports Network." />
      </Helmet>

      {/* Header */}
      <div className="bg-black/50 border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 md:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 md:gap-4">
              <div className="p-2 md:p-3 bg-orange-500">
                <Tv className="w-5 h-5 md:w-6 md:h-6 text-black" />
              </div>
              <div>
                <h1 className="font-secondary text-xl md:text-3xl font-black uppercase text-white">Program Guide</h1>
                <p className="text-zinc-500 text-xs md:text-sm hidden sm:block">Weekly schedule for all channels</p>
              </div>
            </div>

            <div className="flex items-center gap-1 md:gap-2">
              <Button onClick={goToPrevDay} className="p-1.5 md:p-2 bg-zinc-800 hover:bg-zinc-700 text-white h-auto rounded-none">
                <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
              </Button>
              <Button onClick={goToToday} className="px-3 md:px-4 py-1.5 md:py-2 bg-orange-500 hover:bg-orange-400 text-black text-xs md:text-sm font-bold h-auto rounded-none">
                Today
              </Button>
              <Button onClick={goToNextDay} className="p-1.5 md:p-2 bg-zinc-800 hover:bg-zinc-700 text-white h-auto rounded-none">
                <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
              </Button>
            </div>
          </div>

          {/* Day Tabs */}
          <div className="flex gap-1 mt-4 md:mt-6 overflow-x-auto pb-2 scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
            {DAYS.map((day, index) => (
              <button
                key={day}
                onClick={() => setSelectedDay(index)}
                className={`px-3 md:px-4 py-1.5 md:py-2 text-xs md:text-sm font-bold uppercase whitespace-nowrap transition-colors flex-shrink-0 ${
                  selectedDay === index
                    ? "bg-orange-500 text-black"
                    : index === getCurrentDayIndex()
                    ? "bg-zinc-800 text-orange-400 border border-orange-500/30"
                    : "bg-zinc-900 text-zinc-400 hover:text-white hover:bg-zinc-800"
                }`}
                data-testid={`guide-day-${index}`}
              >
                {day.slice(0, 3)}
                {index === getCurrentDayIndex() && (
                  <span className="ml-1.5 w-2 h-2 bg-green-500 rounded-full inline-block animate-pulse" />
                )}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Guide Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        {channels.length === 0 ? (
          <div className="text-center py-16">
            <Tv className="w-20 h-20 text-zinc-700 mx-auto mb-6" />
            <h2 className="font-secondary text-2xl font-bold text-white mb-2">No Channels</h2>
            <p className="text-zinc-500 mb-6">No channels have been configured yet.</p>
          </div>
        ) : (
          <div className="space-y-4 md:space-y-6" data-testid="guide-channels">
            {/* Time Header - Desktop only */}
            <div className="hidden md:flex">
              <div className="w-28 flex-shrink-0" />
              <div className="flex-1 relative">
                <div className="flex">
                  {HOURS.filter(h => h % 2 === 0).map(hour => (
                    <div key={hour} className="flex-1 text-center">
                      <span className="text-xs text-zinc-600 font-mono">{formatTime(hour)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Channel Rows */}
            {channels.map((channel) => {
              const slots = getChannelSlots(channel);
              const nowPlaying = getNowPlaying(channel);
              const has247 = channel.is_24_7_enabled;

              return (
                <motion.div
                  key={channel.channel_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-zinc-900/50 border border-white/5 hover:border-orange-500/30 transition-colors"
                  data-testid={`guide-channel-${channel.channel_id}`}
                >
                  {/* MOBILE: Vertical card layout */}
                  <div className="md:hidden">
                    <Link
                      to={channel.is_conference_network ? `/conference/${channel.slug || channel.channel_id}` : `/watch/${channel.channel_id}`}
                      className="flex items-center gap-3 p-3 border-b border-white/5"
                    >
                      <img
                        src={channel.logo_url || CHANNEL_LOGOS[channel.channel_id] || CHANNEL_LOGOS.channel1}
                        alt={channel.name}
                        className="h-8 w-auto max-w-[60px] object-contain"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-white font-bold text-sm truncate">{channel.name}</h3>
                        {nowPlaying && isToday && (
                          <div className="flex items-center gap-1 text-[10px] text-green-400">
                            <Play className="w-3 h-3" /> <span className="truncate">{nowPlaying}</span>
                          </div>
                        )}
                      </div>
                      {has247 && (
                        <span className="text-[9px] text-orange-400 font-bold uppercase bg-orange-500/10 px-1.5 py-0.5 flex-shrink-0">24/7</span>
                      )}
                    </Link>
                    {/* Mobile schedule items list */}
                    {slots.length > 0 ? (
                      <div className="divide-y divide-white/5">
                        {slots.map(slot => (
                          <div key={slot.id} className={`flex items-center gap-3 px-3 py-2.5 ${
                            slot.type === 'broadcast' ? 'border-l-2 border-l-red-500' 
                            : slot.type === 'playlist' ? 'border-l-2 border-l-zinc-500'
                            : 'border-l-2 border-l-orange-500'
                          }`}>
                            {slot.thumbnailUrl && (
                              <img src={slot.thumbnailUrl} alt="" className="w-12 h-7 object-cover rounded-sm flex-shrink-0 bg-zinc-800" />
                            )}
                            <div className="text-xs text-zinc-500 font-mono w-16 flex-shrink-0">
                              {formatTime(Math.floor(slot.startMin / 60), slot.startMin % 60)}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-white text-sm font-medium truncate">{slot.title}</p>
                              <p className="text-zinc-600 text-[10px]">
                                {formatTime(Math.floor(slot.startMin / 60), slot.startMin % 60)} – {formatTime(Math.floor(slot.endMin / 60), slot.endMin % 60)}
                                {slot.sport && ` · ${slot.sport}`}
                              </p>
                            </div>
                            {isSlotActive(slot) && (
                              <Radio className="w-3 h-3 text-red-500 animate-pulse flex-shrink-0" />
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="px-3 py-3 text-zinc-600 text-xs">No scheduled content</div>
                    )}
                  </div>

                  {/* DESKTOP: Timeline row layout */}
                  <div className="hidden md:flex"
                  >
                  {/* Channel Info */}
                  <Link
                    to={channel.is_conference_network ? `/conference/${channel.slug || channel.channel_id}` : `/watch/${channel.channel_id}`}
                    className="w-28 flex-shrink-0 p-3 border-r border-white/10 hover:bg-orange-500/10 transition-colors group flex items-center justify-center"
                  >
                    <img
                      src={channel.logo_url || CHANNEL_LOGOS[channel.channel_id] || CHANNEL_LOGOS.channel1}
                      alt={channel.name}
                      className="h-12 w-auto max-w-[80px] object-contain group-hover:scale-110 transition-transform"
                      onError={(e) => { e.target.style.display = 'none'; e.target.parentNode.innerHTML = `<span class="text-zinc-500 text-xs font-bold text-center">${channel.name || channel.channel_id}</span>`; }}
                    />
                  </Link>

                  {/* Schedule Timeline */}
                  <div className="flex-1 relative h-20 bg-zinc-950/50">
                    {/* Hour grid lines */}
                    <div className="absolute inset-0 flex">
                      {HOURS.map(hour => (
                        <div key={hour} className="flex-1 border-r border-white/5" />
                      ))}
                    </div>

                    {/* Current time indicator */}
                    {isToday && (
                      <div className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-20" style={{ left: `${currentTime}%` }}>
                        <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-3 h-3 bg-red-500 rounded-full" />
                      </div>
                    )}

                    {/* 24/7 loop background */}
                    {has247 && channel.fill_gaps_with_loop !== false && (
                      <div className="absolute inset-0 bg-zinc-800/30" />
                    )}

                    {/* Scheduled slots */}
                    {slots.map((slot) => {
                      const startPct = (slot.startMin / (24 * 60)) * 100;
                      const endPct = (slot.endMin / (24 * 60)) * 100;
                      const width = endPct - startPct;
                      const active = isSlotActive(slot);
                      const isBroadcast = slot.type === "broadcast";
                      const isPlaylist = slot.type === "playlist";

                      return (
                        <div
                          key={slot.id}
                          className={`absolute top-1 bottom-1 transition-colors cursor-pointer ${
                            active
                              ? isBroadcast
                                ? "bg-red-500 border-2 border-white shadow-lg shadow-red-500/30"
                                : isPlaylist
                                ? "bg-zinc-700 border-2 border-white shadow-lg shadow-zinc-500/30"
                                : "bg-orange-500 border-2 border-white shadow-lg shadow-orange-500/30"
                              : isBroadcast
                              ? "bg-red-500/60 border border-red-500/50 hover:bg-red-500/80"
                              : isPlaylist
                              ? "bg-zinc-700/50 border border-zinc-600/50 hover:bg-zinc-700/70"
                              : "bg-orange-500/60 border border-orange-500/50 hover:bg-orange-500/80"
                          }`}
                          style={{ left: `${startPct}%`, width: `${Math.max(width, 0.5)}%` }}
                          title={`${slot.title}\n${formatTime(Math.floor(slot.startMin / 60), slot.startMin % 60)} - ${formatTime(Math.floor(slot.endMin / 60), slot.endMin % 60)}${slot.sport ? `\n${slot.sport}` : ""}`}
                        >
                          <div className="px-1.5 py-0.5 overflow-hidden h-full flex items-center gap-1.5">
                            {isPlaylist && slot.thumbnailUrl && width > 3 && (
                              <img src={slot.thumbnailUrl} alt="" className="h-full w-auto max-w-[40px] object-cover rounded-sm flex-shrink-0" />
                            )}
                            <div className="overflow-hidden flex-1 min-w-0">
                              <span className={`text-[10px] font-bold truncate block ${active ? "text-white" : "text-white/90"}`}>
                                {slot.title}
                              </span>
                              {width > 4 && (
                                <span className="text-[8px] text-white/50 truncate block">
                                  {formatTime(Math.floor(slot.startMin / 60), slot.startMin % 60)}
                                </span>
                              )}
                            </div>
                          </div>
                          {active && (
                            <div className="absolute top-1 right-1">
                              <Radio className="w-3 h-3 text-white animate-pulse" />
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Empty state */}
                    {slots.length === 0 && (
                      <div className="absolute inset-0 flex items-center justify-center">
                        <span className="text-zinc-600 text-xs">No scheduled content</span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
              );
            })}

            {/* Legend */}
            <div className="flex items-center gap-4 md:gap-6 pt-4 md:pt-6 border-t border-white/10 text-[10px] md:text-xs flex-wrap px-2">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-red-500/60 border border-red-500/50" />
                <span className="text-zinc-400">Scheduled Broadcast</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-orange-500/60 border border-orange-500/50" />
                <span className="text-zinc-400">24/7 Program</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 bg-zinc-700/50 border border-zinc-600/50" />
                <span className="text-zinc-400">24/7 Playlist</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-0.5 h-4 bg-red-500" />
                <span className="text-zinc-400">Current Time</span>
              </div>
              <div className="flex items-center gap-2">
                <Radio className="w-3 h-3 text-white" />
                <span className="text-zinc-400">Now Playing</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GuidePage;
